import {FC, useMemo} from 'react';

//mui
import {Box, Typography} from '@mui/material';
//constants
import {
  AUCTION,
  BODY2,
  CAPTION,
  COLLECTED,
  FOUND_THE_MISSION,
  H5,
  H6,
  OUTSTANDING,
  SUBTITLE1,
  SUBTITLE2,
  TABLES_SALES,
  TOTAL_RAISED,
} from '@/utils/constants';
//icons
import {ReactComponent as EllipseIcon} from '@/assets/icons/ellipse-green.svg';
//styles
import {moneyAmountInfoItemStyle} from './MoneyAmountInfoItem.styles';

import {currencyFormatter} from '@/utils/helpers/currency';
import {useLocation, useNavigate} from 'react-router-dom';
import {ROUTER_URL} from '@/utils/enums';
import {useSelectedSection} from '@/contexts/SelectedSectionContext';
import {Cell, Pie, PieChart, ResponsiveContainer} from 'recharts';

interface MoneyAmountInfoItemProps {
  title: string;
  collected: number;
  outstanding: number;
}

const MoneyAmountInfoItem: FC<MoneyAmountInfoItemProps> = ({title, collected, outstanding}) => {
  const navigate = useNavigate();
  const {pathname} = useLocation();

  const {addSelectedSection} = useSelectedSection();

  const totalAmount = collected + outstanding;

  const handleDonutClick = (type: string) => {
    let sectionName = '';
    const suffix = type === 'collected' ? 'payments' : 'outstanding';

    switch (title) {
      case TOTAL_RAISED:
        sectionName = `Total ${suffix}`;
        break;
      case TABLES_SALES:
        sectionName = `Table ${suffix}`;
        break;
      case AUCTION:
        sectionName = `Auction ${suffix}`;
        break;
      case FOUND_THE_MISSION:
        sectionName = `FTM ${suffix}`;
        break;
      default:
        sectionName = `Total ${suffix}`;
    }

    addSelectedSection(sectionName);

    if (pathname === ROUTER_URL.ORGANIZATION_REPORTS) {
      const tableElement = document.getElementById('reports-table');
      tableElement?.scrollIntoView({behavior: 'smooth', block: 'start'});
      return;
    }

    sessionStorage.setItem('scrollToReportsTable', 'true');
    navigate(ROUTER_URL.ORGANIZATION_REPORTS);
  };

  const chartData = useMemo(() => {
    if (collected === 0 && outstanding === 0) {
      return [{name: 'empty', value: 1, color: '#E0E0E0'}];
    }

    return [
      {name: 'outstanding', value: outstanding, color: 'var(--error-500)'},
      {name: 'collected', value: collected, color: '#00C48C'},
    ];
  }, [collected, outstanding]);

  return (
    <Box sx={moneyAmountInfoItemStyle.wrapper}>
      <Typography variant={SUBTITLE1}>{title}</Typography>

      <Box sx={{...moneyAmountInfoItemStyle.statistics, position: 'relative', width: 170, height: 170}}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={70}
              outerRadius={80}
              paddingAngle={0}
              dataKey="value"
              startAngle={90}
              endAngle={-270}
              stroke="none"
            >
              {chartData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.color}
                  style={{
                    cursor: entry.name === 'empty' ? 'default' : 'pointer',
                    outline: 'none',
                  }}
                  onClick={() => {
                    if (entry.name !== 'empty') {
                      handleDonutClick(entry.name);
                    }
                  }}
                />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>

        <Box
          sx={{
            ...moneyAmountInfoItemStyle.amount,
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            pointerEvents: 'none',
          }}
        >
          <Typography variant={currencyFormatter.format(totalAmount).length > 10 ? H6 : H5}>
            {currencyFormatter.format(totalAmount)}
          </Typography>
          <Typography variant={BODY2} sx={moneyAmountInfoItemStyle.statisticsAmount}>
            {TOTAL_RAISED}
          </Typography>
        </Box>
      </Box>

      <Box sx={moneyAmountInfoItemStyle.content}>
        <Box sx={moneyAmountInfoItemStyle.contentInfo}>
          <Box sx={moneyAmountInfoItemStyle.contentTitle}>
            <EllipseIcon />

            <Typography variant={BODY2} sx={moneyAmountInfoItemStyle.description}>
              {COLLECTED}
            </Typography>
          </Box>
          <Typography variant={SUBTITLE2} sx={moneyAmountInfoItemStyle.totalAmount}>
            {currencyFormatter.format(collected)}
          </Typography>
        </Box>

        <Box sx={moneyAmountInfoItemStyle.contentInfo}>
          <Box sx={moneyAmountInfoItemStyle.contentTitle}>
            <Box sx={moneyAmountInfoItemStyle.outstandingIcon}>
              <EllipseIcon />
            </Box>

            <Typography variant={BODY2} sx={moneyAmountInfoItemStyle.description}>
              {OUTSTANDING}
            </Typography>
          </Box>
          <Typography
            variant={currencyFormatter.format(outstanding).length > 10 ? CAPTION : SUBTITLE2}
            sx={moneyAmountInfoItemStyle.totalAmount}
          >
            {currencyFormatter.format(outstanding)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default MoneyAmountInfoItem;
